import React from 'react';

import cx from 'classnames';
import { Icon } from 'design-system-web';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import {
  ContentNotificationDeliveryTypeEnum,
  CONTENT_NOTIFICATION_DELIVERY_TYPES,
} from 'constants-notifications';
import { FONT_AWESOME_SOLID } from 'constants-activate';

import styles from './NotificationDeliveryTypeCheckboxGroup.scss';

export const SELECTABLE_DELIVERY_TYPES = CONTENT_NOTIFICATION_DELIVERY_TYPES;

const DELIVERY_TYPE_ICON: Record<ContentNotificationDeliveryTypeEnum, string> =
  {
    [ContentNotificationDeliveryTypeEnum.Email]: 'mail',
    [ContentNotificationDeliveryTypeEnum.Push]: 'bell',
    [ContentNotificationDeliveryTypeEnum.SMS]: 'message-square',
  };

const TRANSLATION_KEYS = {
  groupLabel:
    'web.components.cards.NotificationDeliveryTypeCheckboxGroup.groupLabel',
  [ContentNotificationDeliveryTypeEnum.Email]:
    'web.components.cards.NotificationDeliveryTypeCheckboxGroup.email',
  [ContentNotificationDeliveryTypeEnum.Push]:
    'web.components.cards.NotificationDeliveryTypeCheckboxGroup.push',
  [ContentNotificationDeliveryTypeEnum.SMS]:
    'web.components.cards.NotificationDeliveryTypeCheckboxGroup.sms',
};

type NotificationDeliveryTypeCheckboxGroupProps = {
  deliveryTypes: ContentNotificationDeliveryTypeEnum[];
  onChange: (deliveryTypes: ContentNotificationDeliveryTypeEnum[]) => void;
  className?: string;
};

export function getNotificationDeliveryType(
  deliveryTypes: ContentNotificationDeliveryTypeEnum[] | null | undefined,
  t: TFunction
): string {
  return SELECTABLE_DELIVERY_TYPES.filter(type =>
    deliveryTypes?.includes(type)
  )
    .map(type => t(TRANSLATION_KEYS[type]))
    .join(', ');
}

export const NotificationDeliveryTypeCheckboxGroup = ({
  deliveryTypes,
  onChange,
  className,
}: NotificationDeliveryTypeCheckboxGroupProps) => {
  const { t } = useTranslation();

  function toggle(deliveryType: ContentNotificationDeliveryTypeEnum) {
    const isChecked = deliveryTypes.includes(deliveryType);

    if (isChecked && deliveryTypes.length === 1) {
      return;
    }

    const nextSelection = new Set(deliveryTypes);

    if (isChecked) {
      nextSelection.delete(deliveryType);
    } else {
      nextSelection.add(deliveryType);
    }

    onChange(SELECTABLE_DELIVERY_TYPES.filter(type => nextSelection.has(type)));
  }

  return (
    <div
      role="group"
      aria-label={t(TRANSLATION_KEYS.groupLabel)}
      className={cx(styles.container, className)}
    >
      {SELECTABLE_DELIVERY_TYPES.map(deliveryType => {
        const isChecked = deliveryTypes.includes(deliveryType);
        const label = t(TRANSLATION_KEYS[deliveryType]);

        return (
          <label
            key={deliveryType}
            data-is-selected={isChecked}
            className={styles.card}
          >
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => toggle(deliveryType)}
              aria-label={label}
              data-test={`notificationDeliveryTypeCard-${deliveryType}`}
              className={styles.hiddenInput}
            />
            <Icon
              name={DELIVERY_TYPE_ICON[deliveryType]}
              set="Feather"
              size="medium"
              className={styles.icon}
            />
            <span className={styles.label}>{label}</span>
            {isChecked && (
              <Icon
                name="check-circle"
                set="FontAwesome"
                type={FONT_AWESOME_SOLID}
                size="small"
                className={styles.checkIcon}
              />
            )}
          </label>
        );
      })}
    </div>
  );
};
